import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCMSStore } from '../stores/cmsStore';
import { useGalleriesStore } from '../stores/galleriesStore';
import ProfileDetails from './widgets/ProfileDetails';
import GalleriesWidget from './widgets/GalleriesWidget';
import NewestMembers from './widgets/NewestMembers';
import CalendarWidget from './widgets/CalendarWidget';
import NavigationWidget from './widgets/NavigationWidget';
import DashboardWidget from './widgets/DashboardWidget';
import NewsBulletinWidget from './widgets/NewsBulletinWidget';
import Layouts from '../pages/admin/Layouts';
import Lookups from '../pages/admin/Lookups';
import Widgets from '../pages/admin/Widgets';
import Pages from '../pages/admin/Pages';
import ProtectedImage from './ProtectedImage';
import ProtectedVideo from './ProtectedVideo';
import ProtectedVideoThumbnail from './ProtectedVideoThumbnail';

// Widget mapping - maps widget codes to components
const WIDGET_MAP = {
  'PROFILE_DETAILS': ProfileDetails,
  'GALLERIES': GalleriesWidget,
  'NEWEST_MEMBERS': NewestMembers,
  'CALENDAR': CalendarWidget,
  'NAVIGATION': NavigationWidget,
  'DASHBOARD': DashboardWidget,
  'NEWS_BULLETIN': NewsBulletinWidget,
  'LAYOUTS': Layouts,
  'LOOKUPS': Lookups,
  'WIDGETS': Widgets,
  'PAGES': Pages
};

const CMSPage = ({ viewingProfileId }) => {
  const { slug } = useParams();
  const location = useLocation();
  const { accessToken, profile } = useAuthStore();
  const { fetchPage, getPage, isLoading, error } = useCMSStore();
  const { fetchGallery } = useGalleriesStore();
  const [pageData, setPageData] = useState(null);
  const [galleryMedia, setGalleryMedia] = useState({}); // Track media for each gallery
  const [viewingMedia, setViewingMedia] = useState(null); // Track media being viewed in modal

  // Determine page slug based on route
  const getPageSlug = () => {
    if (slug) return slug;
    if (location.pathname === '/') return 'home';
    if (location.pathname === '/members') return 'members';
    // Handle /profile/:profileId/profile route
    if (location.pathname.match(/^\/profile\/\d+\/profile$/)) return 'profile';
    return null;
  };

  const pageSlug = getPageSlug();

  useEffect(() => {
    if (!pageSlug) return;

    const loadPage = async () => {
      // Always fetch fresh data to show latest published version
      const result = await fetchPage(pageSlug, accessToken);
      if (result.success) {
        setPageData(result.data);
      }
    };

    loadPage();
  }, [pageSlug, accessToken]);

  // Load gallery media for all paragraphs with galleries
  useEffect(() => {
    if (!pageData || !pageData.paragraphs) return;

    const loadGalleries = async () => {
      const galleryIds = [];

      // Collect all gallery IDs from paragraphs recursively
      const collectGalleryIds = (paragraphs) => {
        paragraphs.forEach(p => {
          if (p.media_gallery_id && !galleryMedia[p.media_gallery_id]) {
            galleryIds.push(p.media_gallery_id);
          }
          if (p.children) {
            collectGalleryIds(p.children);
          }
        });
      };

      collectGalleryIds(pageData.paragraphs);

      // Load all galleries
      for (const galleryId of galleryIds) {
        const result = await fetchGallery(galleryId, accessToken);
        console.log('FETCHED GALLERY FROM BACKEND:', {
          galleryId,
          layout_code: result.gallery?.layout_code,
          media_layout_id: result.gallery?.media_layout_id,
          fullGallery: result.gallery
        });
        if (result.success && result.gallery?.media) {
          setGalleryMedia(prev => ({
            ...prev,
            [galleryId]: {
              media: result.gallery.media,
              layout_code: result.gallery.layout_code
            }
          }));
        }
      }
    };

    loadGalleries();
  }, [pageData, accessToken]);

  // Carousel state for tracking current slide index per gallery
  const [carouselIndices, setCarouselIndices] = useState({});

  // Render media gallery based on layout - matching Galleries.jsx visualization
  const renderGallery = (galleryId, layoutCode, paragraphLayoutCode = null) => {
    const gallery = galleryMedia[galleryId];
    if (!gallery || !gallery.media || gallery.media.length === 0) return null;

    const media = gallery.media;
    const layout = layoutCode || gallery.layout_code || 'GALLERY_THUMBS';

    // Check if gallery is in a constrained width (FLOAT or CLEAR layout)
    const isConstrained = paragraphLayoutCode &&
      (paragraphLayoutCode.includes('FLOAT') || paragraphLayoutCode.includes('CLEAR'));

    console.log('renderGallery:', { galleryId, layoutCode, resolvedLayout: layout, mediaCount: media.length, paragraphLayoutCode, isConstrained });

    // THUMBNAILS Layout - Grid with gaps (read-only version from Galleries.jsx)
    if (layout === 'GALLERY_THUMBS' || !layout) {
      console.log('Rendering GALLERY_THUMBS');
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
          {media.map((item) => (
            <div
              key={item.id}
              className="relative aspect-square rounded overflow-hidden bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setViewingMedia(item)}
            >
              {item.kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={item.thumbnail_uuid || item.uuid}
                  alt={item.storage_key}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ProtectedVideoThumbnail
                  uuid={item.uuid}
                  className="w-full h-full"
                />
              )}
            </div>
          ))}
        </div>
      );
    }

    // TILES Layout - Smart layout based on context and item count
    if (layout === 'GALLERY_TILES') {
      console.log('Rendering GALLERY_TILES');

      // If constrained (FLOAT/CLEAR), stack all media vertically
      if (isConstrained) {
        return (
          <div className="space-y-0 mb-4">
            {media.map((item) => (
              <div
                key={item.id}
                className="relative w-full bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setViewingMedia(item)}
              >
                {item.kind_code === 'IMAGE' ? (
                  <ProtectedImage
                    uuid={item.banner_uuid || item.uuid}
                    alt={item.storage_key}
                    className="w-full h-auto"
                  />
                ) : (
                  <ProtectedVideoThumbnail
                    uuid={item.uuid}
                    className="w-full h-auto"
                  />
                )}
              </div>
            ))}
          </div>
        );
      }

      // Full width context (ABOVE/BELOW) - smart layout based on count
      const isOdd = media.length % 2 === 1;

      if (isOdd) {
        // Odd number: First item is "hero" (full width), rest shown 2 by 2
        const rows = [];

        // Hero image (first item)
        rows.push(
          <div
            key={media[0].id}
            className="relative w-full bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setViewingMedia(media[0])}
          >
            {media[0].kind_code === 'IMAGE' ? (
              <ProtectedImage
                uuid={media[0].banner_uuid || media[0].uuid}
                alt={media[0].storage_key}
                className="w-full h-auto"
              />
            ) : (
              <ProtectedVideoThumbnail
                uuid={media[0].uuid}
                className="w-full h-auto"
              />
            )}
          </div>
        );

        // Remaining items in pairs
        for (let i = 1; i < media.length; i += 2) {
          rows.push(
            <div key={`pair-${i}`} className="grid grid-cols-2 gap-0">
              <div
                className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setViewingMedia(media[i])}
              >
                {media[i].kind_code === 'IMAGE' ? (
                  <ProtectedImage
                    uuid={media[i].banner_uuid || media[i].uuid}
                    alt={media[i].storage_key}
                    className="w-full h-auto"
                  />
                ) : (
                  <ProtectedVideoThumbnail
                    uuid={media[i].uuid}
                    className="w-full h-auto"
                  />
                )}
              </div>
              <div
                className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setViewingMedia(media[i + 1])}
              >
                {media[i + 1].kind_code === 'IMAGE' ? (
                  <ProtectedImage
                    uuid={media[i + 1].banner_uuid || media[i + 1].uuid}
                    alt={media[i + 1].storage_key}
                    className="w-full h-auto"
                  />
                ) : (
                  <ProtectedVideoThumbnail
                    uuid={media[i + 1].uuid}
                    className="w-full h-auto"
                  />
                )}
              </div>
            </div>
          );
        }

        return <div className="space-y-0 mb-4">{rows}</div>;
      } else {
        // Even number: All items shown 2 by 2
        const rows = [];

        for (let i = 0; i < media.length; i += 2) {
          rows.push(
            <div key={`pair-${i}`} className="grid grid-cols-2 gap-0">
              <div
                className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setViewingMedia(media[i])}
              >
                {media[i].kind_code === 'IMAGE' ? (
                  <ProtectedImage
                    uuid={media[i].banner_uuid || media[i].uuid}
                    alt={media[i].storage_key}
                    className="w-full h-auto"
                  />
                ) : (
                  <ProtectedVideoThumbnail
                    uuid={media[i].uuid}
                    className="w-full h-auto"
                  />
                )}
              </div>
              <div
                className="relative bg-gray-200 dark:bg-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setViewingMedia(media[i + 1])}
              >
                {media[i + 1].kind_code === 'IMAGE' ? (
                  <ProtectedImage
                    uuid={media[i + 1].banner_uuid || media[i + 1].uuid}
                    alt={media[i + 1].storage_key}
                    className="w-full h-auto"
                  />
                ) : (
                  <ProtectedVideoThumbnail
                    uuid={media[i + 1].uuid}
                    className="w-full h-auto"
                  />
                )}
              </div>
            </div>
          );
        }

        return <div className="space-y-0 mb-4">{rows}</div>;
      }
    }

    // CAROUSEL Layout - Full slideshow with navigation (matching Galleries.jsx)
    if (layout === 'GALLERY_CAROUSEL') {
      console.log('Rendering GALLERY_CAROUSEL');
      const currentIndex = carouselIndices[galleryId] || 0;

      return (
        <div className="mb-4">
          <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
            {/* Current slide */}
            <div className="absolute inset-0 flex items-center justify-center">
              {media[currentIndex]?.kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={media[currentIndex]?.uuid}
                  alt="Carousel image"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <ProtectedVideo
                  uuid={media[currentIndex]?.uuid}
                  className="max-w-full max-h-full"
                />
              )}
            </div>

            {/* Navigation arrows - only show if more than 1 item */}
            {media.length > 1 && (
              <>
                <button
                  onClick={() => setCarouselIndices(prev => ({
                    ...prev,
                    [galleryId]: currentIndex === 0 ? media.length - 1 : currentIndex - 1
                  }))}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCarouselIndices(prev => ({
                    ...prev,
                    [galleryId]: (currentIndex + 1) % media.length
                  }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Slide indicators - below the carousel */}
          <div className="flex justify-center gap-2 mt-3">
            {media.map((_, index) => (
              <button
                key={index}
                onClick={() => setCarouselIndices(prev => ({ ...prev, [galleryId]: index }))}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentIndex === index
                    ? 'bg-primary-600 w-8'
                    : 'bg-primary-600 bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderParagraph = (paragraph, depth = 0) => {
    // Helper to render child paragraphs
    const renderChildren = () => {
      if (!paragraph.children || paragraph.children.length === 0) return null;
      return (
        <div className="ml-6">
          {paragraph.children.map((child) => renderParagraph(child, depth + 1))}
        </div>
      );
    };


    // Handle widget paragraphs
    if (paragraph.widget_id && paragraph.widget_code) {
      const WidgetComponent = WIDGET_MAP[paragraph.widget_code];

      if (WidgetComponent) {
        // Parse widget props if available
        const widgetProps = paragraph.widget_props ? JSON.parse(paragraph.widget_props) : {};
        const widgetLayoutCode = paragraph.widget_layout_code;

        const widgetElement = <WidgetComponent profileId={viewingProfileId || profile?.id} {...widgetProps} />;

        // Apply widget layout
        return (
          <div key={paragraph.id} className="mb-4">
            {/* WIDGET_CLEAR_LEFT: Widget left (1/3), content + children right (2/3) */}
            {widgetLayoutCode === 'WIDGET_CLEAR_LEFT' && (
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3">
                  {widgetElement}
                </div>
                <div className="md:w-2/3">
                  {paragraph.content && (
                    <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                  )}
                  {renderChildren()}
                </div>
              </div>
            )}

            {/* WIDGET_CLEAR_RIGHT: Content + children left (2/3), widget right (1/3) */}
            {widgetLayoutCode === 'WIDGET_CLEAR_RIGHT' && (
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-2/3">
                  {paragraph.content && (
                    <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                  )}
                  {renderChildren()}
                </div>
                <div className="md:w-1/3">
                  {widgetElement}
                </div>
              </div>
            )}

            {/* WIDGET_FLOAT_LEFT: Widget floats left (1/3), content + children wrap */}
            {widgetLayoutCode === 'WIDGET_FLOAT_LEFT' && (
              <>
                <div className="float-left mr-8 mb-8 w-full md:w-1/3">
                  {widgetElement}
                </div>
                {paragraph.content && (
                  <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
                <div className="clear-both"></div>
              </>
            )}

            {/* WIDGET_FLOAT_RIGHT: Widget floats right (1/3), content + children wrap */}
            {widgetLayoutCode === 'WIDGET_FLOAT_RIGHT' && (
              <>
                <div className="float-right ml-8 mb-8 w-full md:w-1/3">
                  {widgetElement}
                </div>
                {paragraph.content && (
                  <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
                <div className="clear-both"></div>
              </>
            )}

            {/* WIDGET_ABOVE_PARAGRAPH: Widget above, then content + children */}
            {widgetLayoutCode === 'WIDGET_ABOVE_PARAGRAPH' && (
              <>
                {widgetElement}
                {paragraph.content && (
                  <div className="mt-4 text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
              </>
            )}

            {/* WIDGET_BELOW_PARAGRAPH: Content + children, then widget below */}
            {widgetLayoutCode === 'WIDGET_BELOW_PARAGRAPH' && (
              <>
                {paragraph.content && (
                  <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
                <div className="mt-4">
                  {widgetElement}
                </div>
              </>
            )}

            {/* No layout - widget above, then content + children */}
            {!widgetLayoutCode && (
              <>
                {widgetElement}
                {paragraph.content && (
                  <div className="mt-4 text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
              </>
            )}
          </div>
        );
      } else {
        // Widget not found in mapping
        return (
          <div key={paragraph.id} className="widget-container mb-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-secondary">Widget not implemented: {paragraph.widget_code}</p>
            </div>
          </div>
        );
      }
    }

    // Render content paragraph with optional media
    const renderContent = () => {
      const hasGallery = paragraph.media_gallery_id && galleryMedia[paragraph.media_gallery_id];
      const mediaLayoutCode = paragraph.media_layout_code;

      // DEBUG
      if (hasGallery) {
        console.log('Rendering paragraph with gallery:', {
          paragraphId: paragraph.id,
          galleryId: paragraph.media_gallery_id,
          mediaLayoutCode,
          galleryLayoutCode: galleryMedia[paragraph.media_gallery_id]?.layout_code,
          hasGalleryMedia: !!galleryMedia[paragraph.media_gallery_id],
          paragraph
        });
      }

      return (
        <div key={paragraph.id} className="mb-4">
          {/* CLEAR_LEFT: Gallery left (1/3), content + children right (2/3) */}
          {hasGallery && mediaLayoutCode === 'SINGLE_CLEAR_LEFT' && (
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="md:w-1/3">
                {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
              </div>
              <div className="md:w-2/3">
                {paragraph.content && (
                  <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
              </div>
            </div>
          )}

          {/* CLEAR_RIGHT: Content + children left (2/3), gallery right (1/3) */}
          {hasGallery && mediaLayoutCode === 'SINGLE_CLEAR_RIGHT' && (
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="md:w-2/3">
                {paragraph.content && (
                  <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                )}
                {renderChildren()}
              </div>
              <div className="md:w-1/3">
                {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
              </div>
            </div>
          )}

          {/* FLOAT_LEFT: Gallery floats left (1/3), content + children wrap */}
          {hasGallery && mediaLayoutCode === 'SINGLE_FLOAT_LEFT' && (
            <>
              <div className="float-left mr-8 mb-8 w-full md:w-1/3">
                {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
              </div>
              {paragraph.content && (
                <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
              )}
              {renderChildren()}
              <div className="clear-both"></div>
            </>
          )}

          {/* FLOAT_RIGHT: Gallery floats right (1/3), content + children wrap */}
          {hasGallery && mediaLayoutCode === 'SINGLE_FLOAT_RIGHT' && (
            <>
              <div className="float-right ml-8 mb-8 w-full md:w-1/3">
                {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
              </div>
              {paragraph.content && (
                <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
              )}
              {renderChildren()}
              <div className="clear-both"></div>
            </>
          )}

          {/* ABOVE_PARAGRAPH: Gallery above, then content + children */}
          {hasGallery && mediaLayoutCode === 'SINGLE_ABOVE_PARAGRAPH' && (
            <>
              <div className="mb-4">
                {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
              </div>
              {paragraph.content && (
                <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
              )}
              {renderChildren()}
            </>
          )}

          {/* BELOW_PARAGRAPH: Content + children, then gallery below */}
          {hasGallery && mediaLayoutCode === 'SINGLE_BELOW_PARAGRAPH' && (
            <>
              {paragraph.content && (
                <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
              )}
              {renderChildren()}
              <div className="mt-4">
                {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
              </div>
            </>
          )}

          {/* No layout or gallery without layout - just show gallery above, then content + children */}
          {(!hasGallery || !mediaLayoutCode) && (
            <>
              {hasGallery && (
                <div className="mb-4">
                  {renderGallery(paragraph.media_gallery_id, galleryMedia[paragraph.media_gallery_id]?.layout_code, mediaLayoutCode)}
                </div>
              )}
              {paragraph.content && (
                <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
              )}
              {renderChildren()}
            </>
          )}
        </div>
      );
    };

    return renderContent();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="surface rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-primary">Error Loading Page</h3>
            <p className="mt-2 text-sm text-secondary">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return null;
  }

  return (
    <div>
      {/* Page Content - start directly with paragraphs */}
      {pageData.paragraphs && pageData.paragraphs.length > 0 ? (
        pageData.paragraphs.map((paragraph) => renderParagraph(paragraph))
      ) : (
        <div className="surface rounded-lg shadow-lg p-8">
          <p className="text-secondary text-center py-8">No content available for this page.</p>
        </div>
      )}

      {/* CSS for proper list and content display */}
      <style>{`
        /* Regular lists */
        .paragraph-content ul,
        .paragraph-content ol {
          list-style-position: inside;
          margin: 1em 0;
          padding-left: 1.5em;
        }
        .paragraph-content ul {
          list-style-type: disc;
        }
        .paragraph-content ol {
          list-style-type: decimal;
        }
        .paragraph-content ul ul {
          list-style-type: circle;
        }
        .paragraph-content ul ul ul {
          list-style-type: square;
        }
        .paragraph-content li {
          margin: 0.5em 0;
          display: list-item;
        }

        /* Todo list styles */
        .paragraph-content ul.todo-list {
          list-style: none;
          padding-left: 0;
        }
        .paragraph-content ul.todo-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.5em;
        }
        .paragraph-content ul.todo-list input[type="checkbox"] {
          margin-top: 0.25em;
          cursor: default;
          pointer-events: none;
        }
        .paragraph-content ul.todo-list li span {
          flex: 1;
        }

        /* Headings from rich text */
        .paragraph-content h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        .paragraph-content h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        .paragraph-content h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        .paragraph-content h4 {
          font-size: 1em;
          font-weight: bold;
          margin: 1em 0;
        }

        /* Links */
        .paragraph-content a {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
        }
        .paragraph-content a:hover {
          color: #2563eb;
        }
      `}</style>

      {/* Media Viewer Modal */}
      {viewingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingMedia(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button
              onClick={() => setViewingMedia(null)}
              className="absolute top-4 right-4 p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 z-10"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Media content */}
            <div className="bg-black rounded-lg overflow-hidden">
              {viewingMedia.kind_code === 'IMAGE' ? (
                <ProtectedImage
                  uuid={viewingMedia.uuid}
                  alt={viewingMedia.storage_key}
                  className="w-full h-auto max-h-screen object-contain"
                />
              ) : (
                <ProtectedVideo
                  uuid={viewingMedia.uuid}
                  controls={true}
                  autoPlay={true}
                  className="w-full h-auto max-h-screen"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSPage;
